import React, { useMemo, useState } from 'react';
import { BadgeCheck, Pencil, Trash2 } from 'lucide-react';
import { BoardToolbarExpandSearch } from '../../board/toolbar/BoardToolbarExpandSearch';
import ProcessConfigSkeleton from '../../process-config/ConfigStatusView2Skeleton';
import { UserManagementOptionsMenu } from './UserManagementOptionsMenu';
import { UserTableSortTh } from './UserTableSortTh';
import './UserManagementList.css';

const ALL_TAB = 'all';

export function UserManagementList({
  users,
  displayedUsers,
  searchTerm,
  onSearchChange,
  onNewUser,
  showInactive,
  onShowInactiveChange,
  onPassPolicy,
  onExport,
  exportDisabled = false,
  isLoading = false,
  typeOptions = [],
  sortConfig,
  onSort,
  userProfilePhotoMap,
  getUserTypeLabel,
  getPermissionPills,
  onEditUser,
  onDeleteUser,
  renderProfileAvatar,
}) {
  const [typeTab, setTypeTab] = useState(ALL_TAB);
  const showTypeCol = typeTab === ALL_TAB;

  const tabCounts = useMemo(() => {
    const counts = { [ALL_TAB]: displayedUsers.length };
    typeOptions.forEach((opt) => {
      counts[opt.value] = displayedUsers.filter((user) => getUserTypeLabel(user) === opt.label).length;
    });
    return counts;
  }, [displayedUsers, typeOptions, getUserTypeLabel]);

  const rows = useMemo(() => {
    if (typeTab === ALL_TAB) return displayedUsers;
    const selectedLabel =
      typeOptions.find((opt) => opt.value === typeTab)?.label ?? typeTab;
    return displayedUsers.filter((user) => getUserTypeLabel(user) === selectedLabel);
  }, [displayedUsers, typeTab, typeOptions, getUserTypeLabel]);

  const emptyMessage = (() => {
    if (users.length === 0) return 'Nenhum usuário cadastrado';
    if (displayedUsers.length === 0 && searchTerm.trim()) {
      return `Nenhum usuário encontrado para "${searchTerm}"`;
    }
    if (rows.length === 0) return 'Nenhum usuário neste tipo';
    return null;
  })();

  return (
    <div className="um-list">
      <div className="um-detail-tabs-row">
        <div className="um-detail-tabs" role="tablist" aria-label="Filtrar por tipo de usuário">
          <button
            type="button"
            role="tab"
            aria-selected={typeTab === ALL_TAB}
            className={`um-detail-tab${typeTab === ALL_TAB ? ' is-active' : ''}`}
            onClick={() => setTypeTab(ALL_TAB)}
          >
            Todos
            <span className="um-detail-tab-badge">{tabCounts[ALL_TAB] || 0}</span>
          </button>
          {typeOptions.map((opt) => {
            const on = typeTab === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="tab"
                aria-selected={on}
                className={`um-detail-tab${on ? ' is-active' : ''}`}
                onClick={() => setTypeTab(opt.value)}
              >
                {opt.label}
                <span className="um-detail-tab-badge">{tabCounts[opt.value] || 0}</span>
              </button>
            );
          })}
        </div>
        <div className="um-detail-tabs-actions">
          <BoardToolbarExpandSearch
            value={searchTerm}
            onChange={onSearchChange}
            placeholder="Pesquisar usuários..."
            ariaLabel="Pesquisar usuários"
            forceExpanded
          />
          <button
            type="button"
            className="um-create-user-btn"
            aria-label="Novo usuário"
            onClick={onNewUser}
          >
            <span className="um-create-user-btn-plus" aria-hidden="true">
              +
            </span>
            Novo usuário
          </button>
          <UserManagementOptionsMenu
            showInactive={showInactive}
            onShowInactiveChange={onShowInactiveChange}
            onPassPolicy={onPassPolicy}
            onExport={onExport}
            exportDisabled={exportDisabled}
          />
        </div>
      </div>

      <div className="um-detail-table-wrap">
        {isLoading ? (
          <ProcessConfigSkeleton variant="tableBody" ariaLabel="Carregando usuários" />
        ) : emptyMessage ? (
          <div className="um-detail-empty">{emptyMessage}</div>
        ) : (
          <div
            className={`um-detail-table${showTypeCol ? '' : ' um-detail-table--no-type'}`}
            role="table"
          >
            <div className="um-detail-thead" role="row">
              <UserTableSortTh sortKey="name" label="Nome" sortConfig={sortConfig} onSort={onSort} />
              <UserTableSortTh sortKey="email" label="E-mail" sortConfig={sortConfig} onSort={onSort} />
              <UserTableSortTh sortKey="status" label="Status" sortConfig={sortConfig} onSort={onSort} />
              {showTypeCol ? (
                <UserTableSortTh sortKey="type" label="Tipo" sortConfig={sortConfig} onSort={onSort} />
              ) : null}
              <UserTableSortTh
                sortKey="permissions"
                label="Permissões"
                sortConfig={sortConfig}
                onSort={onSort}
              />
              <span className="um-detail-th um-detail-th-static um-detail-th-actions">Ações</span>
            </div>
            {rows.map((user) => {
              const permissionPills = getPermissionPills(user);
              const isActive = user.active !== false;
              return (
                <div key={user.id} className="um-detail-row" role="row">
                  <div className="um-detail-member">
                    <div className="users-table-member-inner">
                      <div className="users-table-avatar">
                        {renderProfileAvatar(
                          userProfilePhotoMap[String(user.id)] || null,
                          user.username || user.user,
                          'profile-avatar-img--table'
                        )}
                      </div>
                      <div className="users-table-member-text">
                        <span className="users-table-member-name">
                          {user.username || user.user?.split('@')[0] || '—'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <span className="um-detail-email">{user.user || '—'}</span>
                  <span>
                    <span className={`boolean-badge ${isActive ? 'yes' : 'no'}`}>
                      <span className="boolean-badge-dot" />
                      <span className="boolean-badge-text">{isActive ? 'Ativo' : 'Inativo'}</span>
                    </span>
                  </span>
                  {showTypeCol ? (
                    <span className="um-detail-role">{getUserTypeLabel(user)}</span>
                  ) : null}
                  <span className="um-detail-perms">
                    {permissionPills.length > 0 ? (
                      <div className="users-table-perm-wrap">
                        {permissionPills.map((label) => (
                          <span key={label} className="users-table-perm-pill">
                            <BadgeCheck size={12} strokeWidth={2} aria-hidden="true" />
                            {label}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="users-table-perm-empty">—</span>
                    )}
                  </span>
                  <span className="um-detail-actions">
                    <div className="user-actions user-actions--modern">
                      <button
                        type="button"
                        className="btn-edit"
                        onClick={() => onEditUser(user)}
                        title="Editar usuário"
                        aria-label="Editar usuário"
                      >
                        <Pencil size={16} strokeWidth={1.75} />
                      </button>
                      <button
                        type="button"
                        className="btn-delete"
                        onClick={() => onDeleteUser(user)}
                        title="Excluir usuário"
                        aria-label="Excluir usuário"
                      >
                        <Trash2 size={16} strokeWidth={1.75} />
                      </button>
                    </div>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
